import Vue from 'vue';
import ElementUI from 'element-ui';
import 'element-ui/lib/theme-chalk/index.css';
import App from './App.vue';
import VueMaterial from 'vue-material';
import 'vue-material/dist/vue-material.min.css';
import 'vue-material/dist/theme/default.css'; // 默认主题
import 'font-awesome/css/font-awesome.min.css'
import axios from 'axios';
import './assets/global-fixes.css'; // Global fixes for horizontal scrolling
import './assets/tailwind.css'; // Tailwind utilities (Preflight disabled in tailwind.config.js)
import Swal from 'sweetalert2'
import { clearStoredAuth, parseJwtPayload, isJwtExpiredPayload } from '@/utils/auth'
import i18n from '@/i18n'
Vue.prototype.$http = axios

// Set global SweetAlert2 defaults
Swal.mixin({
  confirmButtonColor: '#3bc2ff',
  cancelButtonColor: '#64748b',
  background: '#ffffff',
  customClass: {
    confirmButton: 'swal-confirm-btn',
    cancelButton: 'swal-cancel-btn',
    popup: 'swal-popup'
  }
})

// Default to the `/api` prefix so production deployments can keep the same
// path shape as local development. Dev still relies on vue.config.js proxying.
const apiBaseUrl = (process.env.VUE_APP_API_BASE_URL || '/api').replace(/\/$/, '') || '/api'
axios.defaults.baseURL = apiBaseUrl

function isAbsoluteHttpBase(base) {
  return /^https?:\/\//i.test(base)
}

/** Spring JAR reached directly (no nginx /api), e.g. http://127.0.0.1:8088 */
function isDirectSpringBackendBase(base) {
  try {
    const u = new URL(base)
    if (!u.port || u.port === '') return false
    const p = parseInt(u.port, 10)
    return p !== 80 && p !== 443
  } catch (e) {
    return false
  }
}

function normalizeRequestUrl(requestUrl) {
  if (typeof requestUrl !== 'string' || requestUrl.startsWith('http')) {
    return requestUrl
  }

  if (!requestUrl.startsWith('/api/')) {
    return requestUrl
  }

  const base = String(axios.defaults.baseURL || '')

  // Dev: baseURL `/api` — must strip one /api so axios does not produce /api/api/...
  if (!isAbsoluteHttpBase(base)) {
    return requestUrl.replace(/^\/api/, '') || '/'
  }

  // Tooling / local: full URL to JVM on a high port — Spring has no /api prefix.
  if (isDirectSpringBackendBase(base)) {
    const stripped = requestUrl.replace(/^\/api/, '') || '/'
    if (stripped === '/') return ''
    return stripped.startsWith('/') ? stripped.slice(1) : stripped
  }

  // Build env uses base `https://host/api` — strip once so we do not get /api/api/.
  const baseTrimmed = base.replace(/\/$/, '')
  if (/\/api$/i.test(baseTrimmed)) {
    const stripped = requestUrl.replace(/^\/api/, '') || '/'
    if (stripped === '/') return ''
    return stripped.startsWith('/') ? stripped.slice(1) : stripped
  }

  // Production behind nginx (https://test.sparrownests.com, etc.): keep `/api/...`.
  // Axios resolves `https://host` + `/api/user/login` → `https://host/api/user/login` (correct).
  return requestUrl
}

function buildDebugUrl(requestUrl) {
  if (typeof requestUrl !== 'string' || requestUrl.startsWith('http')) {
    return requestUrl
  }

  const normalizedUrl = normalizeRequestUrl(requestUrl)
  return `${axios.defaults.baseURL}${normalizedUrl}`
}

/** When localStorage token is unreadable/expired — fail before HTTP so we don't send unsigned admin calls → 401. */
function rejectExpiredSession(config) {
  clearStoredAuth()
  const err = new Error('Your session expired. Please sign in again.')
  err.isAxiosError = true
  err.code = 'ERR_SESSION_EXPIRED'
  err.userMessage =
    'Your session expired or is invalid. Sign in again, then retry (admin JWT must reach the reward API).'
  err.config = config
  err.response = { status: 401, statusText: 'Unauthorized', data: null }
  window.dispatchEvent(new CustomEvent('auth-logout'))
  return Promise.reject(err)
}

// Log which API URL is being used (for debugging different environments)
if (process.env.VUE_APP_DEBUG === 'true') {
  console.log(`%c[Sparrow] API Base URL: ${apiBaseUrl}`, 'color: #2196F3; font-weight: bold;')
}

// ✅ FIX 2: Set request headers properly
axios.defaults.headers.post['Content-Type'] = 'application/json'
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

// ✅ FIX 3: Configure request timeout from environment
const apiTimeout = parseInt(process.env.VUE_APP_API_TIMEOUT) || 30000
axios.defaults.timeout = apiTimeout

// ✅ FIX 4: Proper request interceptor with better auth handling
axios.interceptors.request.use(function (config) {
  config.url = normalizeRequestUrl(config.url)
  const requestUrl = String(config.url || '')
  // Debug logging with full URL information
  if (process.env.VUE_APP_DEBUG === 'true') {
    const fullUrl = buildDebugUrl(requestUrl)
    console.log(`%c[Request] ${config.method?.toUpperCase()} ${fullUrl}`, 'color: #4CAF50;')
  }
  
  // ✅ FIX 5: Detect auth endpoints (should NOT have Authorization header)
  const isAuthEndpoint = requestUrl && (
    requestUrl.includes('/api/user/login') ||
    requestUrl.includes('api/user/login') ||
    requestUrl.includes('/api/user/register') ||
    requestUrl.includes('api/user/register') ||
    requestUrl.includes('/api/user/sendMail') ||
    requestUrl.includes('api/user/sendMail') ||
    requestUrl.includes('/api/user/sendMailToo') ||
    requestUrl.includes('api/user/sendMailToo') ||
    requestUrl.includes('/api/user/test-email') ||
    requestUrl.includes('api/user/test-email') ||
    requestUrl.includes('/api/user/forget') ||
    requestUrl.includes('api/user/forget') ||
    requestUrl.includes('/user/login') ||
    requestUrl.includes('/user/register') ||
    requestUrl.includes('/user/sendMail') ||
    requestUrl.includes('/user/sendMailToo') ||
    requestUrl.includes('/user/test-email') ||
    requestUrl.includes('/user/forget')
  );
  
  const token = localStorage.getItem('token');
  if (token && !isAuthEndpoint) {
    const payload = parseJwtPayload(token);
    const isExpired = !payload || isJwtExpiredPayload(payload);

    if (isExpired) {
      if (process.env.VUE_APP_DEBUG === 'true') {
        console.warn('[Auth] Token invalid or expired before request — not sending unsigned call')
      }
      return rejectExpiredSession(config);
    }

    config.headers['token'] = token
    config.headers['Authorization'] = `Bearer ${token}`
    if (process.env.VUE_APP_DEBUG === 'true') {
      console.log('[Auth] Added valid token to request')
    }
  } else if (!isAuthEndpoint && !token && process.env.VUE_APP_DEBUG === 'true') {
    console.warn('[Auth] No token found for protected endpoint:', requestUrl)
  }
  
  return config;
}, function (error) {
  console.error('[Request Error]', error.message);
  return Promise.reject(error);
});

// ✅ FIX 7: Improved response interceptor with helpful error messages
axios.interceptors.response.use(function (response) {
  if (process.env.VUE_APP_DEBUG === 'true') {
    const status = response.status;
    const statusText = response.statusText || 'OK';
    console.log(`%c[Response] ${status} ${statusText}`, 'color: #2196F3;')
  }
  // Backend uses GlobalExceptionHandler that returns HTTP 200 with `{ code: 0, msg }`
  // (and business failures use the same envelope). Treat non-success like a rejected request
  // so callers do not assume the operation succeeded (e.g. forgot-password email flow).
  const data = response && response.data
  if (
    data &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    typeof data.code === 'number' &&
    data.code !== 1
  ) {
    const msg = data.msg != null ? String(data.msg) : 'Request failed'
    // Stack traces point here by design: HTTP 200 with code≠1 becomes a rejected promise.
    const err = new Error(msg)
    err.response = response
    err.userMessage = msg
    return Promise.reject(err)
  }
  return response;
}, function (error) {
  // ✅ FIX 8: Provide helpful error messages for debugging
  const errorDetails = {
    message: error.message,
    url: error.config?.url || 'unknown',
    fullUrl: error.config ? 
      buildDebugUrl(String(error.config.url || ''))
      : 'unknown',
    method: error.config?.method?.toUpperCase() || 'unknown',
    status: error.response?.status || 'no-response',
    statusText: error.response?.statusText || 'unknown',
    timestamp: new Date().toISOString()
  };
  
  // Log: real HTTP errors as errors; offline / refused dev-server as warnings (expected when processes stop).
  const logApiIssue = error.response ? console.error.bind(console) : console.warn.bind(console);
  logApiIssue('[API]', {
    type: error.response ? 'HTTP Error' : error.code || 'Network Error',
    ...errorDetails,
    responseData: error.response?.data || 'no-data'
  });
  
  // Store error details for debugging
  if (process.env.VUE_APP_DEBUG === 'true') {
    console.group('🔴 Full Error Details');
    console.table(errorDetails);
    if (error.response?.data) {
      console.log('Response Body:', error.response.data);
    }
    console.groupEnd();
  }
  
  // Network errors (connection failed, host unreachable, etc.)
  if (!error.response) {
    const usesDevProxy = apiBaseUrl === '/api' || /^\/(?!\/)/.test(apiBaseUrl);
    if (usesDevProxy) {
      error.userMessage =
        'Cannot reach the app or API. For local development: run Spring Boot on port 8088, run `npm run serve` in the frontend folder, and open the printed URL (usually http://localhost:8081).';
    } else {
      const hint = error.message.includes('timeout')
        ? 'Request timeout - server not responding'
        : error.message.includes('ECONNREFUSED')
        ? `Connection refused - check the host/port for ${apiBaseUrl}`
        : error.message.includes('CORS')
        ? 'CORS error - backend not configured for this origin'
        : 'Network error - check that the API server is running';
      error.userMessage = `Failed to reach API server: ${apiBaseUrl}\n\n${hint}`;
    }
  }
  // HTTP errors (400, 401, 404, 500, etc.)
  else if (error.response?.status === 401) {
    error.userMessage = 'Unauthorized - Token expired or invalid. Please login again.';
    // Auto logout on 401
    clearStoredAuth();
    window.dispatchEvent(new CustomEvent('auth-logout'));
  } else if (error.response?.status === 404) {
    error.userMessage = `Not Found (404) - Endpoint might be incorrect: ${error.config?.url}`;
  } else if (error.response?.status === 500) {
    const body = error.response?.data;
    const bodyStr = typeof body === 'string' ? body : '';
    if (bodyStr.includes('Proxy error') || bodyStr.includes('ECONNREFUSED')) {
      error.userMessage =
        'Cannot reach the Java API from the dev server (proxy to port 8088 failed). Start the backend, then try again.';
    } else if (typeof body === 'object' && body !== null && body.msg != null) {
      error.userMessage = String(body.msg);
    } else {
      error.userMessage = 'Server error (500).';
    }
  } else if (error.response?.status >= 400) {
    error.userMessage = `Client error (${error.response.status}) - ${error.response.statusText}`;
  }
  
  return Promise.reject(error);
});

Vue.use(VueMaterial);
// Vue.use(ElementUI);
Vue.use(ElementUI, {
  size: 'large',
  i18n: (key, value) => i18n.t(key, value)
});

Vue.config.productionTip = false

import router from '@/router/index'  //引入router
import { installSiteAnalytics } from '@/utils/siteAnalytics'

installSiteAnalytics(router)

new Vue({
  router,
  i18n,
  render: h => h(App),
}).$mount('#app')
